import React, { useRef, useState, useLayoutEffect } from "react";
import { ProgressBar } from "../progress-bar/ProgressBar";
import "./TreeMenuNew.css";
import { Add, Minus } from "iconsax-react";
import { useTheme, Tooltip, IconButton } from "@mui/material";
import { Icon } from "@iconify/react";
// Utility function to get the total count of expanded children recursively
export const getExpandedChildrenCount = (node) => {
    if (!node.children || !node.isExpanded) {
        return 0;
    }
    let count = 0;
    for (const child of node.children) {
        count += 1; // Count the direct child
        if (child.isExpanded && child.children && child.children.length > 0) {
            count += getExpandedChildrenCount(child); // Recursively count expanded descendants
        }
    }
    return count;
};
const TreeNodeComponent = ({ node, onToggle, onNodeClick, level, isLast, isMobile = false, onMaximizeClick, showMaximize = false, hideChildren = false, }) => {
    const handleToggle = () => {
        if (node.isExpandable) {
            onToggle(node.id);
        }
    };
    const handleNodeClick = () => {
        if (onNodeClick) {
            onNodeClick(node.id);
        }
    };
    const theme = useTheme();
    const hasChildren = node.children && node.children.length > 0;
    const showChildren = node.isExpanded && hasChildren && !hideChildren;
    const nodeRef = useRef(null);
    const [connectionHeight, setConnectionHeight] = useState(39);
    useLayoutEffect(() => {
        if (!nodeRef.current || isLast)
            return;
        const update = () => {
            if (nodeRef.current) {
                setConnectionHeight(nodeRef.current.offsetHeight - 22);
            }
        };
        update();
        const observer = new ResizeObserver(update);
        observer.observe(nodeRef.current);
        return () => observer.disconnect();
    }, [isLast]);
    return (React.createElement("div", { className: "tree-node", ref: nodeRef },
        React.createElement("div", { className: "tree-node-content" },
            level > 0 && (React.createElement("div", { className: "connection-container", style: { left: `${(level - 1) * 24 + 16}px` } },
                React.createElement("div", { className: "vertical-line" }),
                React.createElement("div", { className: `corner-line ${isLast ? "last" : ""}`, style: {
                        "--connection-height": `${connectionHeight}px`,
                    } }))),
            React.createElement("div", { className: "tree-node-button", style: { marginLeft: `${level * 24}px` } }, node.isExpandable ? (React.createElement("button", { className: `expand-button ${node.isExpanded ? "expanded" : "collapsed"}`, onClick: handleToggle }, node.isExpanded ? React.createElement(Minus, null) : React.createElement(Add, null))) : (React.createElement("div", { className: "leaf-button" },
                React.createElement(Add, { color: theme.palette.text.disabled, size: 16, style: { cursor: "default" } })))),
            React.createElement("div", { style: {
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flex: 1,
                    minWidth: 0,
                    opacity: 1,
                } },
                React.createElement("div", { className: `tree-node-info ${node.isActive ? "active" : ""}`, onClick: handleNodeClick, style: { flex: 1 } },
                    React.createElement("div", { className: "tree-node-content-wrapper" },
                        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "6px" } },
                            node.isSaved !== undefined && (React.createElement(Tooltip, { title: node.isSaved
                                    ? "Cijfers opgeslagen"
                                    : "Cijfers niet opgeslagen", arrow: true, placement: "top" },
                                React.createElement("span", { style: {
                                        display: "inline-flex",
                                        alignItems: "center",
                                        flexShrink: 0,
                                    } },
                                    React.createElement(Icon, { icon: node.isSaved ? "mdi:check-circle" : "mdi:alert-circle", width: 16, height: 16, color: node.isSaved ? "#4caf50" : "#f44336" })))),
                            React.createElement(Tooltip, { title: node.tooltipPath || "", arrow: true, placement: "right" },
                                React.createElement("span", { className: "tree-node-label" }, node.label))),
                        node.percentage !== undefined && (React.createElement("div", { className: "tree-node-progress" }, node.isGreyedOut ? (React.createElement(Tooltip, { title: "Niet beschikbaar vanwege het gekozen waarderingsniveau", arrow: true, placement: "top" },
                            React.createElement("span", { style: {
                                    fontSize: "0.75rem",
                                    color: "#999",
                                    cursor: "help",
                                } }, "NB"))) : (React.createElement(ProgressBar, { value: node.percentage, color: node.isActive ? "#3192D0" : "#00244D", barBg: node.isActive ? "#3192D03D" : "#3192D026" })))))),
                showMaximize && isMobile && onMaximizeClick ? (React.createElement(IconButton, { size: "small", sx: {
                        color: theme.palette.tertiary[40],
                    }, onClick: (e) => {
                        e.stopPropagation();
                        onMaximizeClick();
                    } },
                    React.createElement(Icon, { icon: "solar:maximize-linear", width: "20", height: "20" }))) : null)),
        showChildren && (React.createElement("div", { className: "tree-children" }, node.children.map((child, index) => {
            const isLastChild = index === node.children.length - 1;
            const hasNextSiblingChild = index < node.children.length - 1;
            return (React.createElement(TreeNodeComponent, { key: child.id, node: child, onToggle: onToggle, onNodeClick: onNodeClick, level: level + 1, isLast: isLastChild, hasNextSibling: hasNextSiblingChild, siblingCount: node.children.length, isMobile: isMobile, onMaximizeClick: onMaximizeClick, showMaximize: false }));
        })))));
};
export const TreeMenuNew = ({ data, expandedNodes, activeNodeId, onToggle, onNodeClick, className = "", isMobile = false, onMaximizeClick, showOnlyFirst = false, hideChildren = false, }) => {
    // Update nodes with expansion and active state
    const updateNodeState = (nodes) => {
        return nodes.map((node) => (Object.assign(Object.assign({}, node), { isExpanded: expandedNodes.has(node.id), isActive: node.id === activeNodeId, children: node.children ? updateNodeState(node.children) : undefined })));
    };
    const updatedData = updateNodeState(data);
    const nodesToDisplay = showOnlyFirst && isMobile ? updatedData.slice(0, 1) : updatedData;
    return (React.createElement("div", { className: `tree-menu-new ${className}` }, nodesToDisplay.map((node, index) => {
        const isLast = index === nodesToDisplay.length - 1;
        const hasNextSibling = index < nodesToDisplay.length - 1;
        const showMaximize = showOnlyFirst && isMobile && index === 0;
        return (React.createElement(TreeNodeComponent, { key: node.id, node: node, onToggle: onToggle, onNodeClick: onNodeClick, level: 0, isLast: isLast, hasNextSibling: hasNextSibling, siblingCount: nodesToDisplay.length, isMobile: isMobile, onMaximizeClick: onMaximizeClick, showMaximize: showMaximize, hideChildren: hideChildren }));
    })));
};
